import { useState, useRef, useEffect } from "react";
import { useApi } from "../../hooks/use-api";
import { useSingleton } from "../../hooks/use-singleton";
import { useNotify } from "../../hooks/use-notify";
import {
  apiAdminChapters,
  apiAdminUploadContent,
  apiAdminKnowledgeBase,
  apiCourseFilter,
  API_ADMIN_BANKING_SUBJECTS,
  API_ADMIN_STUDENT_SUBJECTS,
  API_ADMIN_UPSC_SUBJECTS,
  API_ADMIN_UPSC_UNITS,
  API_ADMIN_TNPSC_UNITS,
} from "../../utils/api-routes";

const useHandler = () => {
  const { post, get } = useApi();
  const { user } = useSingleton();
  const { toggleNotify } = useNotify();
  const [selectedChapter, setSelectedChapter] = useState("");
  const [customChapter, setCustomChapter] = useState("");
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [bankingSubjects, setBankingSubjects] = useState([]);
  const [upscSubjects, setUpscSubjects] = useState([]);
  const [upscUnits, setUpscUnits] = useState([]);
  const [tnpscUnits, setTnpscUnits] = useState([]);

  const [selectedBankingSubject, setSelectedBankingSubject] = useState("");
  const [selectedUpscSubject, setSelectedUpscSubject] = useState("");
  const [selectedUpscUnit, setSelectedUpscUnit] = useState("");
  const [selectedTnpscUnit, setSelectedTnpscUnit] = useState("");

  const [resolvedContext, setResolvedContext] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isCallingKB, setIsCallingKB] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubjectDropdownOpen, setIsSubjectDropdownOpen] = useState(false);
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false);
  const [hierarchy, setHierarchy] = useState([]);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const subjectDropdownRef = useRef(null);
  const unitDropdownRef = useRef(null);

  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState("");
  const [isExamDropdownOpen, setIsExamDropdownOpen] = useState(false);
  const examDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }

      if (
        subjectDropdownRef.current &&
        !subjectDropdownRef.current.contains(event.target)
      ) {
        setIsSubjectDropdownOpen(false);
      }

      if (
        unitDropdownRef.current &&
        !unitDropdownRef.current.contains(event.target)
      ) {
        setIsUnitDropdownOpen(false);
      }

      if (
        examDropdownRef.current &&
        !examDropdownRef.current.contains(event.target)
      ) {
        setIsExamDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const examType = user?.data?.exam || user?.data?.course;
  
useEffect(() => {
  const userExams = user?.data?.exam
    ?.split(",")
    .map((exam) => exam.trim())
    .filter(Boolean);

  setExams(userExams || []);
}, [user?.data?.exam]);

  const currentExam = ["NEET", "JEE"].includes(examType?.toUpperCase())
    ? selectedExam || examType
    : examType;

  // useEffect(() => {
  //   if (examType) {
  //     fetchHierarchy();
  //   }
  // }, [examType]);

  const fetchHierarchy = async () => {
    try {
      const response = await get(apiCourseFilter(selectedExam));
      if (response?.status === 200) {
        setHierarchy(response?.data?.hierarchy || []);
      }
    } catch (error) {
      console.error("Error fetching hierarchy:", error);
    }
  };

  useEffect(() => {
    if (selectedExam) {
      fetchHierarchy();
    }
  }, [selectedExam]);

  useEffect(() => {
    if (currentExam && hierarchy.length > 0) {
      const type = examType.toLowerCase();
      if (type === "banking") {
        fetchBankingSubjects();
      } else if (type === "upsc") {
        fetchUpscSubjects();
      } else if (type === "tnpsc") {
        fetchTnpscUnits();
      } else {
        fetchChapters();
      }
    }
  }, [currentExam, hierarchy]);

  const fetchBankingSubjects = async () => {
    try {
      const response = await post(API_ADMIN_BANKING_SUBJECTS, {
        exam: examType,
        section: user?.data?.section,
      });
      if (Array.isArray(response?.data?.subjects)) {
        setBankingSubjects(response.data.subjects);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchUpscSubjects = async () => {
    try {
      const response = await post(API_ADMIN_UPSC_SUBJECTS, {
        board: user?.data?.board,
        clas: user?.data?.clas,
        medium: user?.data?.medium,
        paper: user?.data?.paper,
      });
      if (Array.isArray(response?.data?.subjects)) {
        setUpscSubjects(response.data.subjects);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchUpscUnits = async (subject) => {
    try {
      const response = await post(API_ADMIN_UPSC_UNITS, {
        board: user?.data?.board,
        clas: user?.data?.clas,
        medium: user?.data?.medium,
        paper: user?.data?.paper,
        subject: subject,
      });
      if (Array.isArray(response?.data?.units)) {
        setUpscUnits(response.data.units);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchTnpscUnits = async () => {
    try {
      const response = await post(API_ADMIN_TNPSC_UNITS, {
        exam: examType,
        board: user?.data?.board,
        clas: user?.data?.clas,
        medium: user?.data?.medium,
        part: user?.data?.part,
      });
      if (Array.isArray(response?.data?.units)) {
        setTnpscUnits(response.data.units);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (
      selectedBankingSubject ||
      selectedUpscSubject ||
      selectedUpscUnit ||
      selectedTnpscUnit
    ) {
      fetchChapters();
    }
  }, [
    selectedBankingSubject,
    selectedUpscSubject,
    selectedUpscUnit,
    selectedTnpscUnit,
  ]);

  const getMappedKey = (field) => {
    const mapping = {
      exam: "exam",
      subject: "subject",
      clas: "clas",
      board: "board",
      medium: "medium",
      section: "section",
      part: "part",
      unit: "unit",
      chapter: "chapter",
      paper: "paper",
    };
    return mapping[field] || field;
  };

  const getPayloadValue = (field) => {
    if (field === "exam") return selectedExam;
    if (field === "board") return user?.data?.board;
    if (field === "subject") return user?.data?.subject;
    if (field === "clas") return user?.data?.clas;
    if (field === "medium") return user?.data?.medium;

    const userDataKeyMap = {
      clas: "clas",
      medium: "medium",
    };

    const key = userDataKeyMap[field] || field;
    return user?.data?.[key];
  };

  const fetchChapters = async () => {
    try {
      const payload = {};
      const type = currentExam?.toLowerCase();

      // Resolve context for CBSE
      if (
        (type === "cbse" || type === "student" || type === "tn_state_board") &&
        !user?.data?.subject
      ) {
        const subRes = await post(API_ADMIN_STUDENT_SUBJECTS, {
          board: user?.data?.board,
          clas: user?.data?.clas,
          medium: user?.data?.medium,
        });
        const subject = subRes?.data?.subjects?.[0];
        if (subject) {
          payload.subject = subject;
          setResolvedContext((prev) => ({ ...prev, subject }));
        }
      }

      hierarchy.forEach((field) => {
        if (field !== "chapter" && field !== "lesson") {
          const apiKey = getMappedKey(field);

          // Map manually selected values
          let value;
          if (type === "banking" && field === "subject")
            value = selectedBankingSubject;
          else if (type === "upsc" && field === "subject")
            value = selectedUpscSubject;
          else if (type === "upsc" && field === "unit")
            value = selectedUpscUnit;
          else if (type === "tnpsc" && field === "unit")
            value = selectedTnpscUnit;

          if (!value) value = getPayloadValue(field);

          if (value !== undefined && value !== null) {
            payload[apiKey] = value;
          }
        }
      });

      const response = await post(apiAdminChapters(selectedExam), payload);
      const chapterList = response?.data?.chapters || response?.data?.lessons;
      if (Array.isArray(chapterList)) {
        setChapters(chapterList);
      }
    } catch (error) {
      console.error("Error fetching chapters:", error);
    }
  };

  const buildPayload = (isFormData = false) => {
    const data = isFormData ? new FormData() : {};
    const addToPayload = (key, value) => {
      if (value === undefined || value === null) return;
      if (isFormData) {
        data.append(key, value);
      } else {
        data[key] = value;
      }
    };
  addToPayload("exam", selectedExam);

    const type = selectedExam?.toLowerCase();
    hierarchy.forEach((field) => {
      let value;
      const apiKey = getMappedKey(field);

      // if (field === "chapter" || field === "lesson") {
      //   value = selectedChapter;
      // }
      // if (field === "chapter" || field === "lesson") {
      //   value =
      //     selectedChapter === "All Chapters" ? customChapter : selectedChapter;
      // }
      if (field === "chapter" || field === "lesson") {
        value = selectedChapter?.toLowerCase().includes("all")
          ? customChapter
          : selectedChapter;
      } else if (type === "banking" && field === "subject") {
        value = selectedBankingSubject;
      } else if (type === "upsc" && field === "subject") {
        value = selectedUpscSubject;
      } else if (type === "upsc" && field === "unit") {
        value = selectedUpscUnit;
      } else if (type === "tnpsc" && field === "unit") {
        value = selectedTnpscUnit;
      } else if (resolvedContext[field]) {
        value = resolvedContext[field];
      } else {
        value = getPayloadValue(field);
      }

      addToPayload(apiKey, value);
    });

    return data;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
    }
  };

  const resetState = () => {
    setSelectedChapter("");
    setSelectedBankingSubject("");
    setSelectedTnpscUnit("");
    setSelectedUpscSubject("");
    setSelectedUpscUnit("");
    setFile(null);
    setUploadSuccess(false);
    setIsUploading(false);
    setIsCallingKB(false);
  };

  const handleUpload = async () => {
    if (!selectedChapter || !file) return;

    setIsUploading(true);
    try {
      const formData = buildPayload(true);
      formData.append("file", file);

      const response = await post(apiAdminUploadContent(selectedExam), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const formatPath = (path) => path?.split("uploads/")[1] || path;

      if (response?.status === 200) {
        toggleNotify({
          type: "success",
          title: response?.data?.message || "File uploaded successfully",
          description:
            formatPath(response?.data?.path) || "Content uploaded successfully",
        });
        setUploadSuccess(true);
      } else {
        toggleNotify({
          type: "error",
          title:
            response?.data?.message || "Unable to upload file. Please retry",
          description: formatPath(response?.data?.path) || "Upload failed",
        });
      }
    } catch (error) {
      toggleNotify({
        type: "error",
        title: "Error",
        description:
          error?.response?.data?.message ||
          "Something went wrong during upload",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleKnowledgeBaseAction = async () => {
    setIsCallingKB(true);
    try {
      const payload = buildPayload(false);
      const medium =
        user?.data?.medium || user?.data?.medium_of_instruction || "English";

      const mediumType = medium.toLowerCase();

      const response = await post(
        apiAdminKnowledgeBase(selectedExam, mediumType),
        payload,
      );
      if (response?.status === 200) {
        toggleNotify({
          type: "success",
          title: "Success",
          description: response?.data?.message || "Knowledge base updated",
        });
        resetState();
      } else {
        toggleNotify({
          type: "error",
          title: "Error",
          description: response?.data?.message || "Action failed",
        });
      }
    } catch (error) {
      toggleNotify({
        type: "error",
        title: "Error",
        description: error?.response?.data?.message || "Something went wrong",
      });
    } finally {
      setIsCallingKB(false);
    }
  };

  return {
    selectedChapter,
    setSelectedChapter,
    file,
    setFile,
    isDragging,
    chapters,
    isUploading,
    isCallingKB,
    uploadSuccess,
    isDropdownOpen,
    setIsDropdownOpen,
    fileInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileChange,
    resetState,
    handleUpload,
    handleKnowledgeBaseAction,
    bankingSubjects,
    selectedBankingSubject,
    setSelectedBankingSubject,
    upscSubjects,
    selectedUpscSubject,
    setSelectedUpscSubject,
    upscUnits,
    selectedUpscUnit,
    setSelectedUpscUnit,
    tnpscUnits,
    selectedTnpscUnit,
    setSelectedTnpscUnit,
    isSubjectDropdownOpen,
    setIsSubjectDropdownOpen,
    isUnitDropdownOpen,
    setIsUnitDropdownOpen,
    fetchUpscUnits,
    setChapters,
    dropdownRef,
    subjectDropdownRef,
    unitDropdownRef,
    customChapter,
    setCustomChapter,
    selectedExam,
    setSelectedExam,
    exams,
    isExamDropdownOpen,
    setIsExamDropdownOpen,
    examDropdownRef,
  };
};

export default useHandler;
